import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useClickOutside } from '../../hooks/useClickOutside'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import StackedBarChart from '../../components/Charts/StackedBarChart'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { exportToPNG, exportToSVG, exportToCSV } from '../../utils/tmbExportUtils'
import {
    safeNumber,
    findColumnIndex,
    parseMonthToValue,
    resolveMonthIdx,
} from '../../utils/dataUtils'
import {
    getSliderIndicesFromValues,
    initializeSliderValues,
    TMB_SLIDER_KEY,
    extractActualMonthValues,
} from '../../utils/tmbUtils'
import { useTmbSlider } from '../../context/TmbSliderContext'
import { applyLaunchMonthFilterToZohoData } from '../../utils/tmbLaunchDates'
import '../../styles/shared/index.css'
import './PatientRecognition.css'

// Bottom-to-top stacking order (index 0 = bottom of chart)
const RESPONSE_ORDER = [
    'All of the time',
    'Most of the time',
    'A little of the time',
    'None of the time',
] as const

type ResponseCategory = typeof RESPONSE_ORDER[number]

const RESPONSE_COLORS: Record<ResponseCategory, string> = {
    'All of the time': '#38587F',
    'Most of the time': '#4B84A2',
    'A little of the time': '#DE8E47',
    'None of the time': '#90442C',
}

const DATA_LABEL_COLORS = ['#ffffff', '#ffffff', '#ffffff', '#ffffff']

// dataKey matches the 'question' column values from the API (board_use_1..4)
const QUESTION_DEFINITIONS = [
    {
        key: 'Q1',
        dataKey: 'board_use_1',
        displayLabel: 'A member of the team update the names of care team members on the board?',
    },
    {
        key: 'Q2',
        dataKey: 'board_use_2',
        displayLabel: 'The provider discuss preferences and update the board?',
    },
    {
        key: 'Q3',
        dataKey: 'board_use_3',
        displayLabel: 'The provider discuss different care plans and update the board?',
    },
    {
        key: 'Q4',
        dataKey: 'board_use_4',
        displayLabel: 'The provider set a plan for the next huddle and update the board?',
    },
]

const applyLargestRemainder = (rawPcts: number[]): number[] => {
    const floors = rawPcts.map(p => Math.floor(p))
    const remainder = 100 - floors.reduce((s, f) => s + f, 0)
    const diffs = rawPcts.map((p, i) => ({ i, diff: p - floors[i] })).sort((a, b) => b.diff - a.diff)
    const adjusted = [...floors]
    for (let k = 0; k < Math.max(0, remainder); k++) {
        if (diffs[k]) adjusted[diffs[k].i] += 1
    }
    return adjusted
}

interface ColIndices {
    monthIdx: number
    questionIdx: number
    numAllIdx: number
    numMostIdx: number
    numLittleIdx: number
    numNoneIdx: number
    denominatorIdx: number
}

const PatientRecognition: React.FC = () => {
    useEffect(() => {
        const id = 'dm-sans-font-link'
        if (!document.getElementById(id)) {
            const link = document.createElement('link')
            link.id = id
            link.rel = 'stylesheet'
            link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400&display=swap'
            document.head.appendChild(link)
        }
    }, [])

    // ApexCharts captures its SVG width at mount. When the viewport crosses
    // the mobile/desktop breakpoint (e.g. toggling DevTools device emulation),
    // bump this flag so the chart remounts at the new size — much cheaper than
    // window.location.reload() because the API data stays in memory.
    const [isMobileView, setIsMobileView] = useState(
        () => window.matchMedia('(max-width: 900px)').matches
    )
    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 900px)')
        const handleChange = (e: MediaQueryListEvent) => setIsMobileView(e.matches)
        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
    }, [])

    const { globalMonthList } = useTmbSlider()
    const { selectedHospitalIds, refreshKey } = useHospitalSelection()
    const [rawData, setRawData] = useState<any>(null)
    const [actualMonths, setActualMonths] = useState<Set<number>>(new Set())
    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [colIndices, setColIndices] = useState<ColIndices | null>(null)

    const [chartSeries, setChartSeries] = useState<{ name: string; data: number[] }[]>([])
    const [chartColors, setChartColors] = useState<string[]>([])
    const [nValues, setNValues] = useState<number[]>([])
    const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`
    const [sliderRange, setSliderRange] = usePersistedState<[number, number]>(persistenceKey, [0, 0])
    const [sliderStartIdx, sliderEndIdx] = getSliderIndicesFromValues(globalMonthList, sliderRange)

    useEffect(() => {
        if (!globalMonthList.length) return
        setSliderRange(prev => initializeSliderValues(globalMonthList, prev, 12))
    }, [globalMonthList])
    const hasDataForRange = (() => {
        const [minVal, maxVal] = sliderRange
        if (!minVal || !maxVal) return true
        if (!actualMonths.size) return false
        return Array.from(actualMonths).some(m => m >= minVal && m <= maxVal)
    })()

    const [menuOpen, setMenuOpen] = useState(false)
    const chartRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    useClickOutside([menuRef], () => setMenuOpen(false), menuOpen)

    useEffect(() => {
        if (!selectedHospitalIds || selectedHospitalIds.length === 0) {
            setRawData(null)
            setActualMonths(new Set())
            setChartSeries([])
            setNValues([])
            setLoading(false)
            setInitialLoading(false)
            setError(null)
            return
        }

        const hospitalIdsParam = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(',')
            : selectedHospitalIds

        setLoading(true)
        setError(null)

        fetch(`/api/tmb/board_use/${hospitalIdsParam}`)
            .then(r => {
                if (!r.ok) throw new Error(`API error: ${r.status}`)
                return r.json()
            })
            .then(data => {
                applyLaunchMonthFilterToZohoData(data)
                const rows = data?.response?.result?.rows || []
                const columns: string[] = data?.response?.result?.column_order || []

                // No data returned → clear state; hasData check will show empty-state message
                if (!rows.length || !columns.length) {
                    setRawData(null)
                    setActualMonths(new Set())
                    setChartSeries([])
                    setChartColors([])
                    setNValues([])
                    setLoading(false)
                    setInitialLoading(false)
                    return
                }

                setRawData(data)

                const monthIdx = resolveMonthIdx(columns, rows)
                const questionIdx = findColumnIndex(columns, ['question', 'Question', 'question_name', 'Question_Name'])
                const numAllIdx = findColumnIndex(columns, ['num_all_of_the_time', 'all_of_the_time'])
                const numMostIdx = findColumnIndex(columns, ['num_most_of_the_time', 'most_of_the_time'])
                const numLittleIdx = findColumnIndex(columns, ['num_a_little_of_the_time', 'a_little_of_the_time'])
                const numNoneIdx = findColumnIndex(columns, ['num_none_of_the_time', 'none_of_the_time'])
                const denominatorIdx = findColumnIndex(columns, ['denominator', 'Denominator', 'denom', 'total'])

                setColIndices({ monthIdx, questionIdx, numAllIdx, numMostIdx, numLittleIdx, numNoneIdx, denominatorIdx })

                // actualMonths = months that have real survey responses (denominator > 0)
                const dataRows = denominatorIdx >= 0
                    ? rows.filter((r: any[]) => (safeNumber(r[denominatorIdx]) ?? 0) > 0)
                    : rows

                setActualMonths(extractActualMonthValues(dataRows, monthIdx))

                setLoading(false)
                setInitialLoading(false)
            })
            .catch(err => {
                setError(err.message || 'Failed to load data')
                setLoading(false)
                setInitialLoading(false)
            })
    }, [selectedHospitalIds, refreshKey])

    useEffect(() => {
        if (!rawData || !colIndices) {
            setChartSeries([])
            setChartColors([])
            setNValues([])
            return
        }

        const rows: any[][] = rawData?.response?.result?.rows || []
        if (!rows.length) return

        const { monthIdx, questionIdx, numAllIdx, numMostIdx, numLittleIdx, numNoneIdx, denominatorIdx } = colIndices

        // Missing months → do not plot: exclude rows outside the selected range and
        // rows with no survey responses (denominator = 0) from aggregation.
        let filteredRows = rows
        if (monthIdx !== -1) {
            const minVal = sliderRange[0] || 0
            const maxVal = sliderRange[1] || 0
            if (minVal > 0 || maxVal > 0) {
                filteredRows = filteredRows.filter((r: any[]) => {
                    const v = parseMonthToValue(r[monthIdx])
                    return v >= minVal && v <= maxVal
                })
            }
        }
        if (denominatorIdx >= 0) {
            filteredRows = filteredRows.filter((r: any[]) => (safeNumber(r[denominatorIdx]) ?? 0) > 0)
        }

        // Pooled aggregation: sum raw counts and denominators across all months in the
        // selected range, then compute one percentage per question at the end.
        // This avoids the bias of averaging per-month percentages with unequal sample sizes.
        type QCounts = { all: number; most: number; little: number; none: number; denom: number }
        const qMap = new Map<string, QCounts>()

        filteredRows.forEach((r: any[]) => {
            if (!r || r.length === 0) return
            const qCode = questionIdx >= 0 ? String(r[questionIdx] || '').trim().toLowerCase() : ''
            if (!qCode) return

            if (!qMap.has(qCode)) qMap.set(qCode, { all: 0, most: 0, little: 0, none: 0, denom: 0 })
            const c = qMap.get(qCode)!
            c.all += safeNumber(r[numAllIdx]) ?? 0
            c.most += safeNumber(r[numMostIdx]) ?? 0
            c.little += safeNumber(r[numLittleIdx]) ?? 0
            c.none += safeNumber(r[numNoneIdx]) ?? 0
            c.denom += safeNumber(r[denominatorIdx >= 0 ? denominatorIdx : -1]) ?? 0
        })

        // Build percentage arrays in QUESTION_DEFINITIONS order
        const allPcts: number[] = []
        const mostPcts: number[] = []
        const littlePcts: number[] = []
        const nonePcts: number[] = []
        const nPerQuestion: number[] = []

        QUESTION_DEFINITIONS.forEach(def => {
            const c = qMap.get(def.dataKey)
            // Missing response categories → display as 0%: absent or uncounted categories default to 0.
            if (!c || c.denom === 0) {
                allPcts.push(0); mostPcts.push(0); littlePcts.push(0); nonePcts.push(0)
                nPerQuestion.push(0)
                return
            }

            nPerQuestion.push(c.denom)

            const adjusted = applyLargestRemainder([
                c.all / c.denom * 100,
                c.most / c.denom * 100,
                c.little / c.denom * 100,
                c.none / c.denom * 100,
            ])
            allPcts.push(adjusted[0])
            mostPcts.push(adjusted[1])
            littlePcts.push(adjusted[2])
            nonePcts.push(adjusted[3])
        })

        setChartSeries([
            { name: 'All of the time', data: allPcts },
            { name: 'Most of the time', data: mostPcts },
            { name: 'A little of the time', data: littlePcts },
            { name: 'None of the time', data: nonePcts },
        ])
        setChartColors(RESPONSE_ORDER.map(r => RESPONSE_COLORS[r]))
        setNValues(nPerQuestion)
    }, [rawData, colIndices, sliderRange])

    // html2canvas clones SVG attributes, not computed CSS, so text-anchor must be set
    // as an SVG attribute directly before capture and restored after.
    const prepareChartForExport = () => {
        if (!chartRef.current) return []
        const textEls = Array.from(
            chartRef.current.querySelectorAll<SVGTextElement>('.apexcharts-xaxis-label text')
        )
        const saved = textEls.map(el => ({
            el,
            anchor: el.getAttribute('text-anchor') ?? 'middle',
            tspans: Array.from(el.querySelectorAll<SVGTSpanElement>('tspan')).map(t => ({
                t,
                anchor: t.getAttribute('text-anchor') ?? '',
            })),
        }))
        textEls.forEach(el => {
            el.setAttribute('text-anchor', 'start')
            el.querySelectorAll('tspan').forEach(t => t.setAttribute('text-anchor', 'start'))
        })
        return saved
    }

    const restoreChartAfterExport = (saved: ReturnType<typeof prepareChartForExport>) => {
        saved.forEach(({ el, anchor, tspans }) => {
            el.setAttribute('text-anchor', anchor)
            tspans.forEach(({ t, anchor: ta }) => {
                if (ta) t.setAttribute('text-anchor', ta)
                else t.removeAttribute('text-anchor')
            })
        })
    }

    const captureOptions = {
        cssContextClass: 'patient-recognition-page',
    }

    const handleDownloadPNG = async () => {
        if (!chartRef.current) return
        setMenuOpen(false)
        const saved = prepareChartForExport()
        try {
            await exportToPNG(chartRef.current, 'Board_Use', { title: 'Board Use' }, captureOptions)
        } finally {
            restoreChartAfterExport(saved)
        }
    }

    const handleDownloadSVG = async () => {
        if (!chartRef.current) return
        setMenuOpen(false)
        const saved = prepareChartForExport()
        try {
            await exportToSVG(chartRef.current, 'Board_Use', { title: 'Board Use' }, captureOptions)
        } finally {
            restoreChartAfterExport(saved)
        }
    }

    const handleDownloadCSV = () => {
        setMenuOpen(false)
        if (!chartSeries.length) return

        const hospitalStr = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(', ')
            : String(selectedHospitalIds ?? '')

        const [startVal, endVal] = sliderRange
        const startLabel = globalMonthList.find(m => m.value === startVal)?.label
            ?? globalMonthList[0]?.label ?? ''
        const endLabel = globalMonthList.find(m => m.value === endVal)?.label
            ?? globalMonthList[globalMonthList.length - 1]?.label ?? ''

        const dataRows = QUESTION_DEFINITIONS.map((def, qi) => [
            hospitalStr,
            startLabel,
            endLabel,
            def.displayLabel.replace(/\n/g, ' '),
            nValues[qi] ?? 0,
            ...RESPONSE_ORDER.map(r => {
                const s = chartSeries.find(s => s.name === r)
                return s ? `${s.data[qi] ?? 0}%` : '0%'
            }),
        ])

        exportToCSV({
            headers: ['Hospital IDs', 'Start Date', 'End Date', 'Question', 'N count', ...RESPONSE_ORDER],
            rows: dataRows,
            filename: `Board_Use_${new Date().toISOString().split('T')[0]}`,
        })
    }

    if (initialLoading) {
        return (
            <div className="patient-recognition-page tmb-page">
                <LoadingNotice title="Loading board use data..." />
            </div>
        )
    }

    if (error) {
        return (
            <div className="patient-recognition-page tmb-page">
                <div className="content-wrapper">
                    <div className="pr-no-data">No data available</div>
                </div>
            </div>
        )
    }

    const hasData = chartSeries.length > 0 && chartSeries.some(s => s.data.some(v => v > 0))

    return (
        <div id={generatePDFIds.dashboard()} className="patient-recognition-page tmb-page">
            <div className="content-wrapper">
                <div className="header-wrapper">
                    <span className="page-title">Board Use</span>
                    <div className="header-controls">
                        {globalMonthList.length > 0 && (
                            <DateRangeSlider
                                monthList={globalMonthList}
                                startIndex={sliderStartIdx}
                                endIndex={sliderEndIdx}
                                onChange={(start, end) =>
                                    setSliderRange([globalMonthList[start]?.value || 0, globalMonthList[end]?.value || 0])
                                }
                            />
                        )}
                    </div>
                </div>

                <div className="section-card" style={(!hasDataForRange || !hasData) && !loading ? { flex: 'none' } : undefined}>
                    {loading ? (
                        <div className="pr-loading">Loading…</div>
                    ) : !hasDataForRange || !hasData ? (
                        <div className="pr-no-data">No data available</div>
                    ) : (
                        <>
                            <div className="chart-export-wrap" ref={chartRef}>
                                <div className="chart-controls-row">
                                    <span className="chart-heading">How often did..</span>
                                    <div className="custom-download-menu" ref={menuRef}>
                                        <div
                                            className="hamburger-icon"
                                            onClick={() => setMenuOpen(m => !m)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                                <g>
                                                    <rect x="4" y="6" width="16" height="2" rx="1" />
                                                    <rect x="4" y="11" width="16" height="2" rx="1" />
                                                    <rect x="4" y="16" width="16" height="2" rx="1" />
                                                </g>
                                            </svg>
                                        </div>
                                        {menuOpen && (
                                            <div className="download-dropdown show">
                                                <div className="menu-item" onClick={handleDownloadPNG}>Download PNG</div>
                                                <div className="menu-item" onClick={handleDownloadSVG}>Download SVG</div>
                                                <div className="menu-item" onClick={handleDownloadCSV}>Download CSV</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="pr-charts">
                                    {QUESTION_DEFINITIONS.map(def => (
                                        <div key={`q-${def.key}`} className="pr-question">
                                            {def.displayLabel}
                                        </div>
                                    ))}
                                    <div className="pr-legend-spacer" aria-hidden="true" />

                                    {QUESTION_DEFINITIONS.map((def, i) => {
                                        // ApexCharts tooltips fail to initialize when any series
                                        // has all-null data (only the last chart gets a tooltip
                                        // otherwise). Filter out zero-value series entirely and
                                        // align colors accordingly. Also filter out series the
                                        // user has hidden via legend clicks.
                                        const perQuestionSeries: { name: string; data: number[] }[] = []
                                        const perQuestionColors: string[] = []
                                        chartSeries.forEach((s, k) => {
                                            const v = s.data[i] ?? 0
                                            if (v > 0 && !hiddenSeries.has(s.name)) {
                                                perQuestionSeries.push({ name: s.name, data: [v] })
                                                perQuestionColors.push(chartColors[k])
                                            }
                                        })
                                        return (
                                            <div key={`c-${def.key}`} className="pr-chart-stack">
                                                <StackedBarChart
                                                    key={`${def.key}-${isMobileView ? 'mobile' : 'desktop'}`}
                                                    exportFilename={`board_use_${def.key.toLowerCase()}`}
                                                    categories={['']}
                                                    series={perQuestionSeries}
                                                    colors={perQuestionColors}
                                                    height={500}
                                                    yAxisMin={0}
                                                    yAxisMax={100}
                                                    yAxisShow={false}
                                                    xAxisShow={false}
                                                    showGrid={false}
                                                    sparkline={true}
                                                    showDataLabels={true}
                                                    dataLabelColors={DATA_LABEL_COLORS}
                                                    dataLabelMinPercent={4}
                                                    dataLabelFontSize="12px"
                                                    columnWidth="100%"
                                                    fontFamily="Poppins, 'Segoe UI', sans-serif"
                                                    fillOpacity={1}
                                                    hideTooltipTitle={true}
                                                    tooltipFixed={true}
                                                    showXAxisLabels={false}
                                                />
                                            </div>
                                        )
                                    })}

                                    <div className="pr-legend">
                                        {[...RESPONSE_ORDER].reverse().map(name => {
                                            const isHidden = hiddenSeries.has(name)
                                            return (
                                                <div
                                                    key={name}
                                                    className={`pr-legend-item${isHidden ? ' pr-legend-item-hidden' : ''}`}
                                                    onClick={() => setHiddenSeries(prev => {
                                                        const next = new Set(prev)
                                                        if (next.has(name)) next.delete(name)
                                                        else next.add(name)
                                                        return next
                                                    })}
                                                >
                                                    <span
                                                        className="pr-legend-dot"
                                                        style={{ backgroundColor: RESPONSE_COLORS[name] }}
                                                    />
                                                    <span className="pr-legend-label">{name}</span>
                                                </div>
                                            )
                                        })}
                                    </div>

                                    <div className="pr-n-row">
                                        <span className="pr-n-label">N=</span>
                                        {QUESTION_DEFINITIONS.map((def, i) => (
                                            <span key={`n-${def.key}`} className="pr-n-val">{nValues[i] ?? 0}</span>
                                        ))}
                                        <span aria-hidden="true" />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default PatientRecognition
